<template>
  <div class="two-factor-settings">
    <h5>Two-Factor Authentication</h5>

    <div v-if="!twoFactorEnabled && !showSetup" class="alert alert-info">
      <p><strong>Enhance your account security</strong></p>
      <p>Two-factor authentication adds an extra layer of security to your account by requiring a code from your mobile
        device in addition to your password.</p>

      <button class="btn btn-primary" :disabled="isLoading" @click="startSetup">
        <FontAwesomeIcon v-if="isLoading" icon="fa-solid fa-spinner me-2" spin />
        Enable Two-Factor Authentication
      </button>
    </div>

    <div v-else-if="twoFactorEnabled && !showSetup" class="alert alert-success">
      <p><strong>Two-Factor Authentication is enabled</strong></p>
      <p>Your account is protected with two-factor authentication.</p>

      <button class="btn btn-outline-danger" :disabled="isLoading" @click="confirmDisable">
        <FontAwesomeIcon v-if="isLoading" icon="fa-solid fa-spinner me-2" spin />
        Disable Two-Factor Authentication
      </button>
    </div>

    <!-- Setup Process -->
    <div v-if="showSetup" class="mt-4">
      <div class="card">
        <div class="card-header">
          <h6 class="mb-0">Set Up Two-Factor Authentication</h6>
        </div>
        <div class="card-body">
          <!-- QR Code Step -->
          <div v-if="currentStep === 'qr'" class="text-center">
            <p>1. Scan this QR code with your authenticator app:</p>
            <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="2FA QR Code" class="qr-code mb-3" />
            <p class="small text-muted">Or enter this code manually: <code>{{ manualEntryKey }}</code></p>

            <button class="btn btn-primary" @click="currentStep = 'verify'">
              I've Added the Account
            </button>
          </div>

          <!-- Verification Step -->
          <div v-if="currentStep === 'verify'">
            <p>2. Enter the 6-digit code from your authenticator app:</p>
            <z-input v-model="verificationCode" type="text" name="verificationCode" label="Verification Code"
              maxlength="6" placeholder="000000" />
            <div class="d-flex gap-2 mt-3">
              <button class="btn btn-success" :disabled="isLoading || verificationCode.length !== 6"
                @click="confirmSetup">
                <FontAwesomeIcon v-if="isLoading" icon="fa-solid fa-spinner me-2" spin />
                Complete Setup
              </button>
              <button class="btn btn-secondary" @click="cancelSetup">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Dialog -->
    <z-dialog-confirm v-model="showDisableConfirm" confirm-title="Disable Two-Factor Authentication"
      confirm-action="Disable"
      confirm-question="Are you sure you want to disable two-factor authentication? This will make your account less secure."
      @proceed="disableTwoFactor" @closing="showDisableConfirm = false" />

    <!-- Messages -->
    <z-message v-model="message" :class="messageClass" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import ZInput from '@/components/utilities/elements/Input.vue'
import ZMessage from '@/components/utilities/elements/Message.vue'
import ZDialogConfirm from '@/components/utilities/elements/ConfirmDialog.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'TwoFactorSettings',
  components: {
    ZInput,
    ZMessage,
    ZDialogConfirm,
    FontAwesomeIcon
  },
  data() {
    return {
      isLoading: false,
      showSetup: false,
      currentStep: 'qr',
      qrCodeUrl: null,
      manualEntryKey: '',
      verificationCode: '',
      otpauthUri: '',
      message: '',
      messageClass: 'alert alert-info mt-3',
      showDisableConfirm: false
    }
  },
  computed: {
    ...mapState(authStore, ['twoFactorEnabled'])
  },
  methods: {
    ...mapActions(authStore, [
      'initiate2FA',
      'confirm2FA',
      'generate2FAQRCode',
      'disable2FA'
    ]),

    async startSetup() {
      try {
        this.isLoading = true
        this.message = ''

        const result = await this.initiate2FA()

        if (result?.otpauth_uri) {
          this.otpauthUri = result.otpauth_uri
          this.manualEntryKey = result.manualEntryKey || ''

          // Generate QR code
          this.qrCodeUrl = await this.generate2FAQRCode(this.otpauthUri)
          console.log('Generated QR Code URL:', {
            qrCodeUrl: this.qrCodeUrl,
            twoFactorEnabled: this.twoFactorEnabled
          })
          this.showSetup = true
          this.currentStep = 'qr'
        } else {
          throw new Error('Failed to initiate 2FA setup')
        }

        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.message = 'Failed to start 2FA setup. Please try again.'
        this.messageClass = 'alert alert-danger mt-3'
        console.log('2FA setup error:', error)
      }
    },

    async confirmSetup() {
      try {
        this.isLoading = true

        const result = await this.confirm2FA({
          code: this.verificationCode
        })

        if (result?.enabled) {
          this.message = 'Two-factor authentication has been successfully enabled!'
          this.messageClass = 'alert alert-success mt-3'
          this.cancelSetup()
        } else {
          throw new Error('Invalid verification code')
        }

        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.message = 'Invalid verification code. Please try again.'
        this.messageClass = 'alert alert-danger mt-3'
        console.log('2FA confirmation error:', error)
      }
    },

    confirmDisable() {
      this.showDisableConfirm = true
    },

    async disableTwoFactor() {
      try {
        this.isLoading = true
        this.showDisableConfirm = false

        const result = await this.disable2FA()

        if (result?.success) {
          this.message = 'Two-factor authentication has been disabled.'
          this.messageClass = 'alert alert-warning mt-3'
        } else {
          throw new Error('Failed to disable 2FA')
        }

        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.message = 'Failed to disable two-factor authentication. Please try again.'
        this.messageClass = 'alert alert-danger mt-3'
        console.log('2FA disable error:', error)
      }
    },

    cancelSetup() {
      this.showSetup = false
      this.currentStep = 'qr'
      this.qrCodeUrl = null
      this.manualEntryKey = ''
      this.verificationCode = ''
      this.otpauthUri = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.qr-code {
  max-width: 200px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  background: white;
}

.two-factor-settings {
  max-width: 600px;
}
</style>